import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { aiAppVotesAPI, billingAPI } from '../utils/api';
import InfoBanner from './InfoBanner';
import Sidebar from './Sidebar';
import SubscriptionRestrictionModal from './SubscriptionRestrictionModal';

const INACTIVE_SUBSCRIPTION_STATUSES = new Set(['trial', 'expired', 'past_due', 'cancelled']);

/**
 * AppProtectedRoute - Защищенный роут для AI приложений
 * Проверяет авторизацию и активность приложения
 */
function AppProtectedRoute({ children, appId, adminOnly = false }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [appStatus, setAppStatus] = useState({ loading: true, isActive: true, name: '' });
  const [showSubscriptionRestrictionModal, setShowSubscriptionRestrictionModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const trackedUsageKeyRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        setIsAuthenticated(false);
        return false;
      }

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (payload.exp < currentTime) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          setIsAuthenticated(false);
          return false;
        }

        const role = localStorage.getItem('userRole') || 'user';
        setIsAuthenticated(true);
        setUserRole(role);
        return true;
      } catch (error) {
        console.error('Ошибка при проверке токена:', error);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setIsAuthenticated(false);
        return false;
      }
    };

    const checkAppStatus = async () => {
      if (!appId) {
        setAppStatus({ loading: false, isActive: true, name: '' });
        return;
      }

      try {
        const response = await aiAppVotesAPI.getAppInfo(appId);
        setAppStatus({
          loading: false,
          isActive: response.is_active,
          name: response.name
        });
        if (response.is_active) {
          const usageKey = `${appId}:${location.pathname}`;
          if (trackedUsageKeyRef.current !== usageKey) {
            trackedUsageKeyRef.current = usageKey;
            aiAppVotesAPI.recordUsage(appId, 'direct').catch(() => {});
          }
        }
      } catch (error) {
        console.error('Ошибка проверки статуса приложения:', error);
        // При ошибке считаем приложение активным (чтобы не блокировать доступ из-за проблем сети)
        setAppStatus({ loading: false, isActive: true, name: '' });
      }
    };

    const checkSubscriptionStatus = async (role) => {
      if (role === 'admin') {
        setShowSubscriptionRestrictionModal(false);
        return;
      }

      try {
        const subscription = await billingAPI.getSubscription();
        const shouldShowRestriction =
          subscription?.is_active === false &&
          INACTIVE_SUBSCRIPTION_STATUSES.has(subscription?.status);

        setSubscriptionStatus(subscription?.status || '');
        setShowSubscriptionRestrictionModal(Boolean(shouldShowRestriction));
      } catch (subscriptionError) {
        console.error('Ошибка проверки статуса подписки:', subscriptionError);
        setShowSubscriptionRestrictionModal(false);
      }
    };

    if (checkAuth()) {
      checkAppStatus();
      const role = localStorage.getItem('userRole') || 'user';
      checkSubscriptionStatus(role);
    } else {
      setAppStatus(prev => ({ ...prev, loading: false }));
    }
  }, [appId, location.pathname]);

  // Показываем загрузку пока проверяем аутентификацию
  if (isAuthenticated === null || appStatus.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Если не авторизован - редирект на логин
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Если требуются права админа, но их нет
  if (adminOnly && userRole !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Доступ запрещен</h2>
          <p className="mt-2 text-gray-600">У вас недостаточно прав для доступа к этой странице</p>
          <button 
            onClick={() => window.history.back()}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  // Если приложение неактивно
  if (!appStatus.isActive) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
        <Sidebar />
        <main className="lg:ml-64 flex-1 pt-16 lg:pt-0">
          <div className="max-w-2xl mx-auto p-6 lg:p-8">
            <div className="bg-white/90 backdrop-blur-sm border border-gray-200/60 shadow-lg rounded-2xl p-8 text-center">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                Приложение временно недоступно
              </h1>
              
              <p className="text-gray-600 mb-6">
                {appStatus.name ? (
                  <>Приложение <strong>{appStatus.name}</strong> временно отключено администратором.</>
                ) : (
                  <>Данное приложение временно отключено администратором.</>
                )}
              </p>
              
              <p className="text-sm text-gray-500 mb-8">
                Пожалуйста, вернитесь на дашборд или попробуйте позже.
              </p>
              
              <button 
                onClick={() => window.location.href = '/dashboard'}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-600 transition-all shadow-md"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Вернуться на дашборд
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <InfoBanner />
      {children}
      <SubscriptionRestrictionModal
        isOpen={showSubscriptionRestrictionModal}
        status={subscriptionStatus}
        onClose={() => setShowSubscriptionRestrictionModal(false)}
      />
    </>
  );
}

export default AppProtectedRoute;

